import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconMenu2, IconRobot } from '@tabler/icons-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './sheet';
import { useAgentChat } from '../../contexts/AgentChatContext';
import {
  getActiveNavigationArea,
  getActiveNavigationState,
  getPermittedNavigationAreas,
  getPermittedNavigationUtilities
} from '../Navigation/constants';
import { useAuth } from '../../contexts/auth/AuthContext';

const PINNED_AREA_IDS = ['overview', 'creators', 'campaign-work'];

function BottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { openSidebar } = useAgentChat();
  const { hasPermission, user } = useAuth();
  const areas = getPermittedNavigationAreas(hasPermission, user);
  const utilities = getPermittedNavigationUtilities(hasPermission, user);
  const activeArea = getActiveNavigationArea(location.pathname, areas);
  const activeLeafId = getActiveNavigationState(location.pathname).activeId;
  const [selectedAreaId, setSelectedAreaId] = useState(activeArea?.id || areas[0]?.id || null);

  useEffect(() => {
    if (activeArea?.id) setSelectedAreaId(activeArea.id);
  }, [activeArea?.id]);

  const selectedArea = useMemo(
    () => areas.find(area => area.id === selectedAreaId) || areas[0] || null,
    [areas, selectedAreaId]
  );
  const pinnedAreas = areas.filter(area => PINNED_AREA_IDS.includes(area.id));
  const isMoreActive = Boolean(
    moreOpen
    || (activeArea && !PINNED_AREA_IDS.includes(activeArea.id))
    || utilities.some(item => item.id === activeLeafId)
  );

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-glass-border bg-surface-1 sm:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="flex h-14 items-center justify-around">
          {pinnedAreas.map(area => (
            <Link
              key={area.id}
              to={area.path}
              aria-current={activeArea?.id === area.id ? 'page' : undefined}
              className={`flex h-full min-h-[44px] w-full flex-col items-center justify-center gap-0.5 text-[10px] ${
                activeArea?.id === area.id ? 'text-accent' : 'text-text-tertiary'
              }`}
            >
              <area.icon className="h-5 w-5" aria-hidden="true" />
              <span>{area.label}</span>
            </Link>
          ))}
          <button
            type="button"
            onClick={openSidebar}
            className="flex h-full min-h-[44px] w-full flex-col items-center justify-center gap-0.5 text-[10px] text-accent"
          >
            <IconRobot className="h-5 w-5" aria-hidden="true" />
            <span>AI Agent</span>
          </button>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            aria-controls="mobile-navigation-more"
            className={`flex h-full min-h-[44px] w-full flex-col items-center justify-center gap-0.5 text-[10px] ${
              isMoreActive ? 'text-accent' : 'text-text-tertiary'
            }`}
          >
            <IconMenu2 className="h-5 w-5" aria-hidden="true" />
            <span>More</span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          id="mobile-navigation-more"
          side="bottom"
          className="max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain pb-[calc(2rem+env(safe-area-inset-bottom,0px))]"
        >
          <SheetHeader className="sticky top-0 z-10 -mx-6 -mt-6 border-b border-glass-border bg-surface-1 px-6 pb-4 pt-6">
            <SheetTitle>Navigate</SheetTitle>
            <SheetDescription>Choose an area, then the task you need.</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-5">
            <section aria-labelledby="mobile-navigation-areas">
              <h2 id="mobile-navigation-areas" className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Areas
              </h2>
              <div className="grid grid-cols-3 gap-2">
                {areas.map(area => (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => setSelectedAreaId(area.id)}
                    aria-pressed={selectedArea?.id === area.id}
                    className={`flex min-h-[72px] flex-col items-center justify-center gap-2 p-3 text-center ${
                      selectedArea?.id === area.id
                        ? 'bg-accent-muted text-accent'
                        : 'bg-surface-2 text-text-secondary hover:bg-glass-bg-hover'
                    }`}
                  >
                    <area.icon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-xs font-medium">{area.label}</span>
                  </button>
                ))}
              </div>
            </section>

            {selectedArea && (
              <section aria-labelledby="mobile-navigation-current-area">
                <h2 id="mobile-navigation-current-area" className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                  {selectedArea.label}
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {selectedArea.items.map(item => {
                    const className = `flex min-h-[48px] items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium ${
                      item.id === activeLeafId
                        ? 'bg-accent-muted text-accent'
                        : 'bg-surface-2 text-text-secondary hover:bg-glass-bg-hover'
                    }`;

                    return item.external ? (
                      <a
                        key={item.id}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMoreOpen(false)}
                        className={className}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        key={item.id}
                        to={item.path}
                        onClick={() => setMoreOpen(false)}
                        aria-current={item.id === activeLeafId ? 'page' : undefined}
                        className={className}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {utilities.length > 0 && (
              <section aria-labelledby="mobile-navigation-utilities">
                <h2 id="mobile-navigation-utilities" className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                  Utilities
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {utilities.map(item => (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={() => setMoreOpen(false)}
                      aria-current={item.id === activeLeafId ? 'page' : undefined}
                      className={`flex min-h-[48px] items-center px-3 py-2 text-xs font-medium ${
                        item.id === activeLeafId
                          ? 'bg-accent-muted text-accent'
                          : 'bg-surface-2 text-text-secondary hover:bg-glass-bg-hover'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default React.memo(BottomNav);
